// Module that encapsulates all the server requests that are made in the guided setup wizard for registration.

import * as Sentry from '@sentry/browser';
import axios from 'axios';
import api from 'core/api';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { ChurnZeroService } from 'frontend-toolkit';
import { isEqual } from 'lodash';
import { Waiver } from './types';

export const createFormRequest = async (
  variables,
  submit,
  responseCallback
) => {
  try {
    const response = await submit({ variables });

    if (response?.data?.createRegistrationForm?.id) {
      responseCallback(response?.data?.createRegistrationForm?.id);
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;

      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const updateFormRequest = async (
  variables,
  formId,
  submit,
  responseCallback?
) => {
  try {
    const response = await submit({ variables: { ...variables, id: formId } });

    if (response?.data?.updateRegistrationForm?.id) {
      if (responseCallback) {
        responseCallback(response?.data?.updateRegistrationForm?.id);
      }
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;

      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const createParticipantGroupRequest = async (variables, submit) => {
  try {
    const response = await submit({ variables });

    if (response?.data) {
      ChurnZeroService.trackChurnZeroEvent(
        'Gemini - Participant Group created'
      );
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;

      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const updateParticipantGroupRequest = async (variables, submit) => {
  try {
    const response = await submit({ variables });

    if (!response?.data) {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;

      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const deleteParticipantGroupRequest = async (variables, submit) => {
  try {
    const response = await submit({ variables });

    if (!response?.data) {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      // const [errors] = error.graphQLErrors;

      // This is a quick fix. We are not checking errors, but making the assumption we receive a 500 due to registrants already being associated to the participant group.
      reactiveFeedbackMessage({
        // eslint-disable-next-line quotes
        message:
          'Oh snap!  You aren\'t able to delete this group as there are already participants registered.',
        isError: true,
        autoclose: true,
      });

      return 'ERROR';
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const handleFormFieldsRequest = async (
  newFields,
  oldFields,
  formId,
  createSubmit,
  updateSubmit,
  deleteSubmit,
  responseCallback
) => {
  try {
    const temporaryFields = [...newFields];

    // Delete fields are fields that are not internal, locked, ngb fields or doesn't exist in the new fields array.
    const deleteFields = oldFields.filter(
      (f) =>
        !newFields.find((n) => n.id === f.id) &&
        !f.internal &&
        !f.locked &&
        !(f.externalId || f.externalType)
    );

    if (
      newFields.length === 0 &&
      oldFields.length > 0 &&
      oldFields.filter(
        (f) => !f.internal && !f.locked && !(f.externalId || f.externalType)
      ).length === deleteFields.length
    ) {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });

      return;
    }

    temporaryFields.forEach(async (field) => {
      if (
        field.id === null &&
        !oldFields.find(
          (f) => f.form_field_definition_id === field.form_field_definition_id
        )
      ) {
        const variables = {
          formId: formId,
          formFieldDefinitionId: field.form_field_definition_id,
          ...field,
        };
        const response = await createSubmit({ variables });
        if (response?.data?.createRegistrationFormField?.id) {
          const index = temporaryFields.findIndex((f) => f.name === field.name);
          const item = {
            ...temporaryFields[index],
            id: response?.data?.createRegistrationFormField?.id,
          };
          temporaryFields[index] = item;
        } else {
          reactiveFeedbackMessage({
            message:
              'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
            isError: true,
            autoclose: true,
          });
        }
      } else if (
        field.id !== null &&
        !oldFields.find((f) => isEqual(f, field))
      ) {
        const variables = {
          formId: formId,
          formFieldDefinitionId: field.form_field_definition_id,
          ...field,
        };

        const response = await updateSubmit({ variables });
        if (!response?.data?.updateRegistrationFormField?.id) {
          reactiveFeedbackMessage({
            message:
              'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
            isError: true,
            autoclose: true,
          });
        }
      }
    });

    deleteFields.forEach(async (field) => {
      const variables = {
        formId: formId,
        formFieldDefinitionId: field.form_field_definition_id,
        ...field,
        enabled: false,
      };

      const response = await updateSubmit({ variables }).catch((error) => {
        const fields = {
          newFields,
          oldFields,
          deleteFields,
        };
        console.log(JSON.stringify(fields));
        Sentry.captureMessage(error);
        throw error;
      });

      if (!response?.data?.updateRegistrationFormField?.id) {
        reactiveFeedbackMessage({
          message:
            'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
          isError: true,
          autoclose: true,
        });
      }
    });

    responseCallback(temporaryFields);
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;
      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const handleFormWaiversRequest = async (
  waivers: Waiver[],
  formId,
  createSubmit,
  updateSubmit,
  responseCallback
) => {
  try {
    for (const waiver of waivers) {
      if (waiver.id === null) {
        const variables = {
          formId,
          title: waiver.title,
          content: waiver.content,
          signatureType: waiver.signatureType,
          active: waiver.active,
          filename: waiver.filename,
        };

        const response = await createSubmit({ variables });
        const waiverId = response.data?.createRegistrationFormWaiver.id;
        responseCallback({ ...waiver, id: waiverId });
      } else {
        const variables = {
          formId,
          id: waiver.id,
          title: waiver.title,
          content: waiver.content,
          signatureType: waiver.signatureType,
          active: waiver.active,
          filename: waiver.filename || null,
        };

        await updateSubmit({ variables });
        responseCallback(waiver);
      }
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;
      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const handleFormMessageRequest = async (
  message,
  formId,
  createSubmit,
  updateSubmit,
  responseCallback
) => {
  try {
    if (message.id === null) {
      const variables = {
        formId,
        content: message.content,
      };

      const response = await createSubmit({ variables });
      responseCallback({
        ...message,
        id: response.data?.createRegistrationFormMessage.id,
      });
    } else {
      const variables = {
        formId,
        id: message.id,
        content: message.content,
      };

      await updateSubmit({ variables });
      responseCallback(message);
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;
      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};

export const handleWaiverAttachmentUploadRequest = async (
  formId,
  waiverTitle,
  attachment
) => {
  const endpoint = `/forms/${formId}/waivers/upload_url`;

  const { data: file } = await api.axios.get(endpoint, {
    params: {
      file_extension: attachment.name.split('.').pop(),
      waiver_title: waiverTitle,
    },
  });
  axios.put(file.data.url, attachment, {
    headers: { 'Content-Type': file.data.content_type },
  });

  return file;
};

export const handleCreateOrUpdatePartnerOfferingsRequest = async (
  variables,
  submit
) => {
  try {
    const response = await submit({ variables });

    if (!response?.data) {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  } catch (error) {
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;

      reactiveFeedbackMessage({
        message: errors.message,
        isError: true,
        autoclose: true,
      });
    } else {
      reactiveFeedbackMessage({
        message:
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }
  }
};
